import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import ProdutosList from './ProdutosList';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Grid,
  Chip,
  TextField,
  makeStyles,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@material-ui/core';
import { req } from 'src/services/api';
import { savePDF } from '@progress/kendo-react-pdf';
import { ORDER_STATUS, getOrderStatusOptions } from 'src/constants/orderStatus';
import {
  getRefundStatusColor,
  getRefundStatusLabel,
  canAttemptOrderRefund,
  isOrderRefundBlockedByPayment,
  shouldShowOrderRefundSection
} from 'src/constants/refundStatus';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiFormLabel-root': {
      color: 'orange',
      fontWeight: 'bold'
    },
    '& .MuiCardHeader-subheader': {
      color: 'orange',
      fontWeight: 'bold'
    },
    '& .MuiOutlinedInput-input': {
      color: 'gray'
    }
  },
  card: {
    width: '100%'
  },
  refundCard: {
    overflow: 'hidden',
    width: '100%'
  },
  refundSummary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    minWidth: 0
  },
  refundAttemptsHeader: {
    alignItems: 'center',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 12,
    minWidth: 0,
    paddingTop: 12
  },
  refundRecord: {
    border: '1px solid #eee',
    borderRadius: 4,
    marginTop: 12,
    minWidth: 0,
    overflow: 'hidden',
    padding: 12
  },
  refundRecordHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    minWidth: 0
  },
  breakWords: {
    overflowWrap: 'break-word',
    wordBreak: 'break-word'
  },
  refundAttemptsList: {
    maxHeight: 480,
    minWidth: 0,
    overflowY: 'auto',
    paddingRight: 4
  },
  technicalDetails: {
    marginTop: 8,
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto'
  },
  codeBlock: {
    backgroundColor: '#fafafa',
    border: '1px solid #eee',
    borderRadius: 4,
    color: '#555',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.5,
    margin: 0,
    minWidth: 0,
    overflowX: 'auto',
    padding: 12,
    whiteSpace: 'pre'
  },
  refundFooter: {
    backgroundColor: '#fff',
    borderTop: '1px solid #eee',
    bottom: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: 16,
    position: 'sticky',
    zIndex: 1
  },
  refundActionButton: {
    color: 'white',
    '@media (max-width: 600px)': {
      width: '100%'
    }
  }
}));

const ProfileDetails = ({ className, uuid, permissions, ...rest }) => {
  const classes = useStyles();
  const [values, setValues] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [IsConverting, setConverting] = useState(false);
  const [tempoEntrega, setTempoEntrega] = useState();
  const [order, setOrder] = useState(null);
  const [statusInterno, setStatusInterno] = useState('');
  const [refundRecords, setRefundRecords] = useState([]);
  const [isRetryingRefund, setIsRetryingRefund] = useState(false);
  const [expandedRefundRecordId, setExpandedRefundRecordId] = useState(null);
  const [isRefundRecordsExpanded, setIsRefundRecordsExpanded] = useState(false);
  const refPageToPdf = useRef(null);

  const [states, setStates] = useState([]);

  async function convertePageToPDF() {
    await setConverting(true);
    let element = refPageToPdf.current || document.body;
    savePDF(element, {
      scale: 0.5,
      margin: 2,
      paperSize: [595, 845],
      fileName: `Pedido_${order.user.nome}_${order.uuid}`
    });
    setConverting(false);
  }

  async function atualizarStatusInterno(value) {
    if (statusInterno === value) return;
    try {
      const data = await req(
        `
          mutation{
            updateOrder(uuid: "${order.uuid}", fields:{
              status_interno: "${statusInterno}"
            })
          }
          `
      );
      if (data?.errors) {
        toast.error('Não foi possível salvar as observações');
        setStatusInterno(value);
      }
    } catch (err) {}
  }

  async function loadOrder() {
    try {
      const data = await req(
        `
          {
            getOrderById(uuid: "${uuid}")
            {
              uuid
              status
              frete
              valor_total
              parcelas
              data_pedido
              previsao_entrega
              observacao
              status_interno
              deliveryType
              desconto_pix
              desconto_extra
              vendedor
              dataDeFaturamento
              faturado
              emissaoNota
              refundStatus
              refundAttempts
              refundErrorMessage
              canRetryRefund
              isRefundable
              paymentMethod
              {
                type
              }
              ordered_products 
              {
                uuid
                nome_formal
                codigo_produto
                marca_produto
                preco_compra
                quantidade_pedida
              }
              user
              {
                nome
                cpf
                telefone
              }
              cep
              cidade
              bairro
              zona
              logradouro
              numero
              complemento
              nome_destinatario
              ticket{
                value
                codigo
              }
            }
          }
        `
      );
      if (data?.data) {
        const currentOrder = data.data.getOrderById;
        setOrder(currentOrder);
        setValues(currentOrder);
        setTempoEntrega(currentOrder.previsao_entrega);
        setProdutos(currentOrder.ordered_products);
        setStatusInterno(currentOrder.status_interno);
        setStates(getOrderStatusOptions(currentOrder.deliveryType));
      } else if (data?.errors) {
        toast.error(data.errors[0].message);
      }
    } catch (err) {
      console.log(err);
    }
  }

  async function loadRefundRecords() {
    try {
      const data = await req(
        `
          {
            order_refund_records(uuid: "${uuid}")
            {
              id
              status
              attempts
              errorMessage
              requestJsonText
              responseJsonText
              createdAt
              updatedAt
            }
          }
        `
      );

      if (data?.data) {
        setRefundRecords(data.data.order_refund_records || []);
      } else if (data?.errors) {
        toast.error(data.errors[0].message);
      }
    } catch (err) {
      console.log(err);
    }
  }

  async function executeRetryRefund() {
    setIsRetryingRefund(true);
    try {
      const data = await req(
        `
          mutation{
            retry_order_refund(uuid: "${order.uuid}")
          }
        `
      );

      if (data?.data) {
        toast.success(
          data.data.retry_order_refund || 'Estorno realizado com sucesso!'
        );
      } else if (data?.errors) {
        toast.error(data.errors[0].message);
      }
      await loadOrder();
      await loadRefundRecords();
    } catch (err) {
      toast.error('Não foi possível tentar o estorno novamente.');
    } finally {
      setIsRetryingRefund(false);
    }
  }

  function retryRefund() {
    const hasAttempts = (order.refundAttempts ?? 0) > 0;

    confirmAlert({
      title: 'Confirmar estorno',
      message: hasAttempts
        ? 'Deseja tentar o estorno deste pedido novamente?'
        : 'Deseja tentar o estorno deste pedido?',
      buttons: [
        {
          label: 'Sim',
          onClick: executeRetryRefund
        },
        {
          label: 'Não'
        }
      ]
    });
  }

  function toggleRefundTechnicalDetails(recordId) {
    setExpandedRefundRecordId(currentId =>
      currentId === recordId ? null : recordId
    );
  }

  function formatTechnicalJson(value) {
    if (!value) {
      return '';
    }

    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (err) {
      return value;
    }
  }

  useEffect(() => {
    if (uuid) {
      loadOrder();
      loadRefundRecords();
    }
  }, []);

  const handleChange = event => {
    setValues({
      ...values,
      [event.target.name]: event.target.value
    });
  };

  const handleChangeTime = event => {
    setTempoEntrega(event.target.value);
  };

  const alterar_status = () => {
    confirmAlert({
      title: 'Confirme para enviar',
      message: 'Você tem certeza dessa alteração ?',
      buttons: [
        {
          label: 'Sim',
          onClick: async () => {
            if (
              values.status === String(ORDER_STATUS.IN_TRANSIT) &&
              order.deliveryType == 1
            ) {
              try {
                const data = await req(
                  `
                    mutation{
                      updateOrder(uuid: "${order.uuid}", fields:{
                        status: ${values.status}
                        data_envio: "${new Date().toISOString()}"
                        previsao_entrega: "${tempoEntrega}"
                      })
                    }
                    `
                );
                if (data.data) {
                  toast.success('Status atualizado com sucesso!');
                } else if (data.errors) {
                  toast.error(data.errors[0].message);
                }
              } catch (err) {
                console.log(err);
              }
            } else if (
              values.status === String(ORDER_STATUS.PREPARING_SHIPMENT) &&
              order.deliveryType == 2
            ) {
              try {
                const data = await req(
                  `
                    mutation{
                      updateOrder(uuid: "${order.uuid}", fields:{
                        status: ${values.status}
                        previsao_entrega: "${tempoEntrega}"
                      })
                    }
                    `
                );
                if (data.data) {
                  toast.success('Status atualizado com sucesso!');
                } else if (data.errors) {
                  toast.error(data.errors[0].message);
                }
              } catch (err) {
                console.log(err);
              }
            } else if (
              values.status === String(ORDER_STATUS.READY_FOR_PICKUP) &&
              order.deliveryType == 2
            ) {
              try {
                const data = await req(
                  `
                    mutation{
                      updateOrder(uuid: "${order.uuid}", fields:{
                        status: ${values.status}
                        previsao_entrega: "${tempoEntrega}"
                      })
                    }
                    `
                );
                if (data.data) {
                  toast.success('Status atualizado com sucesso!');
                } else if (data.errors) {
                  toast.error(data.errors[0].message);
                }
              } catch (err) {
                console.log(err);
              }
            } else if (values.status === String(ORDER_STATUS.COMPLETED)) {
              try {
                const data = await req(
                  `
                    mutation{
                      updateOrder(uuid: "${order.uuid}", fields:{
                        status: ${values.status}
                        data_entrega: "${new Date().toISOString()}"
                      })
                    }
                    `
                );
                if (data.data) {
                  toast.success('Status atualizado com sucesso!');
                } else if (data.errors) {
                  toast.error(data.errors[0].message);
                }
              } catch (err) {
                console.log(err);
              }
            } else if (values.status === String(ORDER_STATUS.CANCELLED)) {
              try {
                const data = await req(
                  `
                    mutation{
                      cancel_order(uuid: "${order.uuid}")
                    }
                    `
                );
                if (data.data) {
                  toast.success('Status atualizado com sucesso!');
                } else if (data.errors) {
                  toast.error(data.errors[0].message);
                }
              } catch (err) {
                toast.error('Houve um erro ao cancelar');
              }
            } else {
              try {
                const data = await req(
                  `
                    mutation{
                      updateOrder(uuid: "${order.uuid}", fields:{
                        status: ${values.status}
                      })
                    }
                    `
                );
                if (data.data) {
                  toast.success('Status atualizado com sucesso!');
                } else if (data.errors) {
                  toast.error(data.errors[0].message);
                }
              } catch (err) {
                console.log(err);
              }
            }
          }
        },
        {
          label: 'Não',
          onClick: () => {}
        }
      ]
    });
  };

  async function handleEmissaoNota() {
    const data = await req(`
      mutation{ updateOrder(uuid: "${
        order.uuid
      }", fields: { emissaoNota: ${!order.emissaoNota} }) }
    `);
    if (data.data) {
      loadOrder();
    }
  }

  return (
    <>
      {order !== null && values !== null ? (
        <form
          autoComplete="off"
          noValidate
          className={clsx(classes.root, className)}
          {...rest}
        >
          <Card className={classes.card} id="divToPrint" ref={refPageToPdf}>
            {order.faturado && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <CardHeader
                  title={`Pedido faturado em ${new Date(
                    parseInt(order.dataDeFaturamento)
                  ).toLocaleDateString()} às ${new Date(
                    parseInt(order.dataDeFaturamento)
                  )
                    .toLocaleTimeString()
                    .slice(0, 5)} por ${order.vendedor}.`}
                />
                <FormControlLabel
                  disabled={!permissions.alterarStatus}
                  control={
                    <Checkbox
                      checked={order.emissaoNota}
                      onChange={handleEmissaoNota}
                      style={{
                        color: '#f18750'
                      }}
                    />
                  }
                  label="Emissão de nota"
                />
              </div>
            )}
            <Divider />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <CardHeader
                title={
                  'Código do Pedido: ' + order.uuid.slice(30, 36).toUpperCase()
                }
                subheader={new Date(
                  parseInt(order.data_pedido, 10)
                ).toLocaleString('pt-BR')}
              />
              <Button
                style={{
                  marginRight: '1rem',
                  color: 'white',
                  height: '2.5rem',
                  display: IsConverting ? 'none' : 'flex'
                }}
                color="primary"
                variant="contained"
                onClick={convertePageToPDF}
              >
                Download PDF
              </Button>
            </div>

            <Divider />

            <CardContent>
              <Grid container spacing={3}>
                <Grid md={8} xs={6} style={{ marginLeft: '12px' }}>
                  <Typography style={{ fontWeight: 'bold' }}>
                    Dados do Cliente:
                  </Typography>
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Nome"
                    name="name"
                    value={order.user.nome}
                    disabled
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="CPF/CNPJ"
                    name="cpf/cnpj"
                    disabled
                    value={order.user.cpf}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    name="phone"
                    disabled
                    value={order.user.telefone}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Inscrição estadual"
                    name="inscription"
                    disabled
                    value={
                      order.user.cpf.length > 14
                        ? order.user.inscricao
                        : 'Cliente sem inscrição estadual'
                    }
                    variant="outlined"
                  />
                </Grid>
                {order.deliveryType == 1 ? (
                  <>
                    <Grid item xs={12}>
                      <Typography style={{ fontWeight: 'bold' }}>
                        Endereço:
                      </Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Destinatário"
                        name="addressee"
                        disabled
                        value={order.nome_destinatario}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={3} xs={6}>
                      <TextField
                        fullWidth
                        label="CEP"
                        name="addressee"
                        disabled
                        value={order.cep}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={3} xs={6}>
                      <TextField
                        fullWidth
                        label="Cidade"
                        name="city"
                        disabled
                        value={order.cidade}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Bairro"
                        name="neighborhood"
                        disabled
                        value={order.bairro}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Zona"
                        name="zone"
                        disabled
                        value={order.zona}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Logradouro"
                        name="patio"
                        disabled
                        value={
                          order.logradouro +
                          (order.numero ? ', n° ' + order.numero : '')
                        }
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={12} xs={12}>
                      <TextField
                        fullWidth
                        label="Complemento"
                        name="complement"
                        disabled
                        value={order.complemento}
                        variant="outlined"
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Endereço"
                      disabled
                      value="Retirada na loja"
                      variant="outlined"
                    />
                  </Grid>
                )}
                <Grid md={8} xs={6} style={{ marginLeft: '12px' }}>
                  <Typography style={{ fontWeight: 'bold' }}>
                    Pedido:
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Forma de pagamento"
                    name="paymentMethod"
                    disabled
                    value={
                      !order.paymentMethod
                        ? '-'
                        : order.paymentMethod.type === 1
                        ? 'Pix'
                        : order.paymentMethod.type === 2
                        ? 'Cartão de Crédito'
                        : 'Finalizado com vendedor'
                    }
                    variant="outlined"
                  />
                </Grid>
                {order.paymentMethod?.type === 2 && (
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Opção de pagamento"
                      name="paymentOption"
                      disabled
                      value={`${order.parcelas} x ${(
                        order.valor_total / order.parcelas
                      ).toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL'
                      })}`}
                      variant="outlined"
                    />
                  </Grid>
                )}
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Tipo de entrega"
                    name="deliveryType"
                    disabled
                    value={
                      order.deliveryType == 1
                        ? 'Entrega pela Constru.rapi'
                        : 'Retirada pelo usuário'
                    }
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={12} xs={12}>
                  <TextField
                    fullWidth
                    label="Observações"
                    name="obs"
                    disabled
                    value={
                      order.observacao
                        ? order.observacao
                        : 'Pedido sem observações'
                    }
                    variant="outlined"
                  />
                </Grid>

                {shouldShowOrderRefundSection(order) && (
                  <Grid item xs={12}>
                    <Card className={classes.refundCard}>
                      <CardContent>
                        <Box className={classes.refundSummary}>
                          <Box style={{ minWidth: 0 }}>
                            <Typography style={{ fontWeight: 'bold' }}>
                              Estorno do pedido
                            </Typography>
                            {order.refundErrorMessage && (
                              <Typography
                                className={classes.breakWords}
                                color="error"
                                variant="body2"
                              >
                                {order.refundErrorMessage}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={getRefundStatusLabel(order.refundStatus)}
                            size="small"
                            style={{
                              backgroundColor: getRefundStatusColor(
                                order.refundStatus
                              ),
                              color: 'white'
                            }}
                          />
                        </Box>

                        <Box className={classes.refundAttemptsHeader}>
                          <Typography style={{ fontWeight: 'bold' }}>
                            Tentativas (
                            {refundRecords.length || order.refundAttempts || 0})
                          </Typography>
                          <Button
                            color="primary"
                            onClick={() =>
                              setIsRefundRecordsExpanded(current => !current)
                            }
                            size="small"
                          >
                            {isRefundRecordsExpanded ? 'Recolher' : 'Expandir'}
                          </Button>
                        </Box>

                        <Collapse in={isRefundRecordsExpanded}>
                          <Box className={classes.refundAttemptsList}>
                            {refundRecords.length === 0 && (
                              <Typography
                                color="textSecondary"
                                style={{ marginTop: 12 }}
                                variant="body2"
                              >
                                Nenhuma tentativa registrada.
                              </Typography>
                            )}
                            {refundRecords.map((record, index) => {
                              const hasTechnicalDetails =
                                record.requestJsonText ||
                                record.responseJsonText;
                              const isExpanded =
                                expandedRefundRecordId === record.id;

                              return (
                                <Box
                                  className={classes.refundRecord}
                                  key={record.id}
                                >
                                  <Box className={classes.refundRecordHeader}>
                                    <Typography style={{ fontWeight: 'bold' }}>
                                      Tentativa {index + 1}
                                    </Typography>
                                    <Chip
                                      label={getRefundStatusLabel(
                                        record.status
                                      )}
                                      size="small"
                                      style={{
                                        backgroundColor: getRefundStatusColor(
                                          record.status
                                        ),
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    className={classes.breakWords}
                                    color="textSecondary"
                                    style={{ marginTop: 8 }}
                                    variant="body2"
                                  >
                                    Data/hora:{' '}
                                    {new Date(record.createdAt).toLocaleString(
                                      'pt-BR'
                                    )}
                                  </Typography>
                                  {record.responseJsonText && (
                                    <Typography
                                      className={classes.breakWords}
                                      style={{ marginTop: 8 }}
                                      variant="body2"
                                    >
                                      Resposta disponível nos detalhes técnicos.
                                    </Typography>
                                  )}
                                  {record.errorMessage && (
                                    <Typography
                                      className={classes.breakWords}
                                      color="error"
                                      style={{ marginTop: 8 }}
                                      variant="body2"
                                    >
                                      Erro: {record.errorMessage}
                                    </Typography>
                                  )}
                                  {hasTechnicalDetails && (
                                    <>
                                      <Button
                                        color="primary"
                                        onClick={() =>
                                          toggleRefundTechnicalDetails(
                                            record.id
                                          )
                                        }
                                        size="small"
                                        style={{ marginTop: 8, paddingLeft: 0 }}
                                      >
                                        {isExpanded
                                          ? 'Ocultar detalhes técnicos'
                                          : 'Ver detalhes técnicos'}
                                      </Button>
                                      <Collapse in={isExpanded}>
                                        <Box
                                          className={classes.technicalDetails}
                                        >
                                          {record.requestJsonText && (
                                            <Box style={{ marginBottom: 8 }}>
                                              <Typography variant="caption">
                                                Requisição
                                              </Typography>
                                              <pre
                                                className={classes.codeBlock}
                                              >
                                                {formatTechnicalJson(
                                                  record.requestJsonText
                                                )}
                                              </pre>
                                            </Box>
                                          )}
                                          {record.responseJsonText && (
                                            <Box>
                                              <Typography variant="caption">
                                                Resposta
                                              </Typography>
                                              <pre
                                                className={classes.codeBlock}
                                              >
                                                {formatTechnicalJson(
                                                  record.responseJsonText
                                                )}
                                              </pre>
                                            </Box>
                                          )}
                                        </Box>
                                      </Collapse>
                                    </>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </CardContent>
                      {(canAttemptOrderRefund(order) ||
                        isOrderRefundBlockedByPayment(order)) && (
                        <Box className={classes.refundFooter}>
                          <Tooltip
                            title={
                              isOrderRefundBlockedByPayment(order)
                                ? 'O pedido não foi pago, então não pode ser solicitado um reembolso.'
                                : ''
                            }
                          >
                            <span>
                              <Button
                                className={classes.refundActionButton}
                                color="primary"
                                disabled={
                                  isRetryingRefund ||
                                  isOrderRefundBlockedByPayment(order)
                                }
                                onClick={retryRefund}
                                variant="contained"
                              >
                                {isRetryingRefund
                                  ? 'Tentando estorno...'
                                  : (order.refundAttempts ?? 0) > 0
                                  ? 'Tentar estorno novamente'
                                  : 'Tentar estorno'}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                )}
                <Grid item md={5} xs={12}>
                  <TextField
                    fullWidth
                    label="Status do pedido"
                    name="status"
                    onChange={handleChange}
                    required
                    select
                    SelectProps={{ native: true }}
                    value={values.status}
                    variant="outlined"
                    disabled={Number(order.status) === ORDER_STATUS.CANCELLED}
                  >
                    {states.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                {((values.status == ORDER_STATUS.IN_TRANSIT &&
                  order.deliveryType == 1) ||
                  (values.status == ORDER_STATUS.PREPARING_SHIPMENT &&
                    order.deliveryType == 2) ||
                  (values.status == ORDER_STATUS.READY_FOR_PICKUP &&
                    order.deliveryType == 2)) && (
                  <Grid item md={3} xs={12}>
                    <TextField
                      fullWidth
                      id="datetime-local"
                      label="Previsão"
                      type="time"
                      value={tempoEntrega}
                      onChange={handleChangeTime}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                )}
                {permissions.alterarStatus && (
                  <Grid
                    item
                    md={
                      (values.status == ORDER_STATUS.IN_TRANSIT &&
                        order.deliveryType == 1) ||
                      (values.status == ORDER_STATUS.PREPARING_SHIPMENT &&
                        order.deliveryType == 2) ||
                      (values.status == ORDER_STATUS.READY_FOR_PICKUP &&
                        order.deliveryType == 2)
                        ? 4
                        : 7
                    }
                    xs={12}
                    style={{ display: 'flex', alignItems: 'flex-end' }}
                  >
                    <Button
                      style={{
                        height: 56,
                        color: 'white',
                        display: IsConverting ? 'none' : 'flex'
                      }}
                      color="primary"
                      variant="contained"
                      disabled={
                        Number(order.status) === ORDER_STATUS.CANCELLED ||
                        (values.status === ORDER_STATUS.IN_TRANSIT &&
                          tempoEntrega === '' &&
                          order.deliveryType == 1) ||
                        (values.status === ORDER_STATUS.PREPARING_SHIPMENT &&
                          tempoEntrega === '' &&
                          order.deliveryType == 2) ||
                        (values.status === ORDER_STATUS.READY_FOR_PICKUP &&
                          tempoEntrega === '' &&
                          order.deliveryType == 2)
                      }
                      onClick={event => alterar_status()}
                    >
                      Atualizar Status
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    rowsMax={4}
                    label="Observações (status interno)"
                    name="status_interno"
                    value={statusInterno}
                    variant="outlined"
                    onChange={e => setStatusInterno(e.target.value)}
                    onBlur={() => atualizarStatusInterno(order.status_interno)}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <Divider />

            <Divider />

            <div
              style={{
                display: 'flex',
                alignItems: 'left',
                paddingTop: 10,
                paddingLeft: '2rem',
                paddingRight: 20,
                flexDirection: 'column'
              }}
            >
              <Typography style={{ marginBottom: '1rem' }}>
                <b> Produtos:</b>
              </Typography>

              <ProdutosList
                ordered_products={produtos}
                reduz={IsConverting}
                frete={order.frete}
                desconto_pix={order.desconto_pix}
                ticket={order.ticket}
                desconto_extra={order.desconto_extra}
                valor_total={order.valor_total}
              />
            </div>
          </Card>
        </form>
      ) : (
        <></>
      )}
    </>
  );
};

ProfileDetails.propTypes = {
  className: PropTypes.string
};

export default ProfileDetails;
